import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CommunitySearch from "./Community_UI/CommunitySearch";
import { del, get, put } from "../../utility/fetch";
import Spinner from "../Spinner";
import "./Community.css";
import notification from "../../utility/notification";

// Tooltip Component with click-outside functionality
const Tooltip = ({ children, onClose }) => {
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    return (
        <div ref={ref} className="tooltip-modal">
            {children}
        </div>
    );
};

const CommunityPage = () => {
    // Tab state: joined, created, friends, or discover
    const [activeTab, setActiveTab] = useState("joined");
    // Search state (shared across views for demo purposes)
    const [searchQuery, setSearchQuery] = useState(null);
    const [communityList, setCommunityList] = useState([]);
    const [groupLoading, setGroupLoading] = useState(false);
    const [showTooltip, setShowTooltip] = useState(null);
    const navigate = useNavigate();

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const getCommunity = async () => {
        setGroupLoading(true);
        try {
            const response = await get(`/Community/list`);
            if (response.statusCode === 200) {
                setCommunityList(response?.data);
            }
        } catch (error) {
            console.error("Error fetching community data:", error);
        }
        setGroupLoading(false);
    };

    const delCommunity = async (id) => {
        setGroupLoading(true);
        try {
            const response = await del(`/Community/${id}`);
            if (response.message === 'Community Deleted Successfully') {
                getCommunity();
                notification({message: "Community Deleted Successfully", type: "success"});
            }
        } catch (error) {
            notification({message: "Error deleting community", type: "error"});
            console.error("Error deleting community data:", error);
        }
        setGroupLoading(false);
    };

    const leaveCommunity = async (id, communityId) => {
        setGroupLoading(true);
        try {
            const response = await put(`/Community/${id}/community-member/${communityId}/leave-community`);
            if (response.statusCode === 200) {
                notification({message: "Left Community", type: "success"});

                getCommunity();
            }
        } catch (error) {
            notification({message: "Error leaving community", type: "error"});
            console.error("Error leaving community data:", error);
        }
        setGroupLoading(false);
    };

    const searchCommunity = async (query) => {
        setGroupLoading(true);
        try {
            const response = await get(
                `/Community/filter-list/CommunityName/${query}/1/10`
            );
            if (response.statusCode === 200) {
                setCommunityList(response?.data?.recordList);
            }
        } catch (error) {
            console.error("Error fetching community data:", error);
        }
        setGroupLoading(false);
    };

    const toggleTooltip = (groupId) => {
        setShowTooltip((prev) => (prev === groupId ? null : groupId));
    };

    const handleDelete = (groupId) => {
        delCommunity(groupId);
        setShowTooltip(null);
    };

    const handleLeave = (groupId, communityMemberId) => {
        leaveCommunity(groupId, communityMemberId);
        setShowTooltip(null);
    };

    // Filtering logic based on group type
    const filterGroups = (groupType) => {
        const currentUserId = Number(sessionStorage.getItem("userId"));
        if (groupType === "created") {
            return communityList.filter(
                (community) => community.createdBy === currentUserId
            );
        } else if (groupType === "joined") {
            return communityList.filter((community) =>
                community.communityMembers.some(
                    (member) => member.patient.id === currentUserId
                )
            );
        }
        return [];
    };

    // Render groups grid with improved tooltip positioning
    const renderGroupsGrid = (groupType, headerText) => {
        const filteredGroups = filterGroups(groupType);
        return (
            <>
                <div className="flex space-between m-b-40 m-t-20">
                    <div>
                        <h3 className="group-name">{headerText}</h3>
                        <p className="community-description">
                            {filteredGroups.length} groups
                        </p>
                    </div>
                    <div className="float-right m-l-40">
                        <div className="search-container">
                            <input
                                type="text"
                                placeholder="Search for Groups"
                                value={searchQuery || ""}
                                onChange={handleSearchChange}
                            />
                            <span className="filter-icon">🔍</span>
                        </div>
                    </div>
                </div>
                <div className="groups-grid">
                    {filteredGroups.map((group) => {
                        const isCreatedByUser =
                            group.createdBy === Number(sessionStorage.getItem("userId"));
                        return (
                            <div className="group-card" key={group.id}>
                                <div className="group-info">
                                    <div className="flex gap-10">
                                        <img
                                            src={group.pictureUrl || "/user-avater.png"}
                                            alt="Group"
                                            className="profile-pic"
                                        />
                                        <span>
                                            <h3 className="group-name">{group.name}</h3>
                                            <p className="group-details">{group.description}</p>
                                        </span>
                                    </div>
                                    <div className="flex gap-10 space-between group-action-container">
                                        <button
                                            className="submit-btn"
                                            onClick={() => navigate("/group")}
                                        >
                                            View Group
                                        </button>
                                        {/* Button container set to relative so that the tooltip is positioned absolutely */}
                                        <div className="tooltip-wrapper">
                                            <button
                                                className="save-drafts"
                                                onClick={() => toggleTooltip(group.id)}
                                            >
                                                ...
                                            </button>
                                            {showTooltip === group.id && (
                                                <Tooltip onClose={() => setShowTooltip(null)}>
                                                    {isCreatedByUser && (
                                                        <div
                                                            className="tooltip-item"
                                                            onClick={() => handleDelete(group.id)}
                                                        >
                                                            🗑️ Delete
                                                        </div>
                                                    )}
                                                    <div className="tooltip-item"
                                                        onClick={() => handleLeave(group?.id, group?.communityMembers[0]?.id)}
                                                    >🚪 Exit Group</div>
                                                    <div className="tooltip-item">🔗 Share Link</div>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </>
        );
    };

    useEffect(() => {
        if (searchQuery) {
            searchCommunity(searchQuery);
        } else {
            getCommunity();
        }
    }, [searchQuery]);

    return (
        <div className="community-container">
            <div className="flex space-between">
                <div className="profile-section">
                    <img
                        src="/user-avater.png"
                        alt="User Profile"
                        className="profile-img"
                    />
                    <div>
                        <h2>Good morning!</h2>
                        <p>{sessionStorage.getItem("userName") || ""}</p>
                    </div>
                </div>
                <div className="w-60">
                    <div className="search-container float-right w-40">
                        <input
                            type="text"
                            placeholder="Search for Groups"
                            value={searchQuery || ""}
                            onChange={handleSearchChange}
                        />
                        <span className="filter-icon">🔍</span>
                    </div>
                </div>
            </div>

            <div className="flex space-between m-t-20 m-b-10">
                <h2 className="section-title">Community</h2>
                <div className="w-30">
                    <button
                        className="submit-btn"
                        onClick={() => navigate("/create-group")}
                    >
                        + Create Group
                    </button>
                </div>
            </div>
            <p className="community-description">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Lacus, nulla a
                accumsan at morbi bibendum tortor id a. Nullam amet, ultricies orci
                ultricies odio condimentum.
            </p>

            {/* Tabs */}
            <div className="com-tabs">
                <span
                    className={`com-tab ${activeTab === "joined" ? "active" : ""}`}
                    onClick={() => setActiveTab("joined")}
                >
                    Groups Joined
                </span>
                <span
                    className={`com-tab ${activeTab === "created" ? "active" : ""}`}
                    onClick={() => setActiveTab("created")}
                >
                    Groups Created
                </span>
                <span
                    className={`com-tab ${activeTab === "friends" ? "active" : ""}`}
                    onClick={() => setActiveTab("friends")}
                >
                    Friends
                </span>
                <span
                    className={`com-tab ${activeTab === "discover" ? "active" : ""}`}
                    onClick={() => setActiveTab("discover")}
                >
                    Discover
                </span>
            </div>

            {/* Conditional rendering based on the active tab */}
            {activeTab === "joined" &&
                renderGroupsGrid("joined", "All Groups You've Joined")}
            {activeTab === "created" &&
                renderGroupsGrid("created", "All Groups You've Created")}
            {activeTab === "friends" && (
                <div className="coming-soon">
                    <h3>Friends</h3>
                    <p>Friends functionality coming soon.</p>
                </div>
            )}
            {activeTab === "discover" && (
                <div>
                    {groupLoading ? (
                        <div className="spinner">
                            <Spinner />
                        </div>
                    ) : (
                        <CommunitySearch data={communityList} />
                    )}
                </div>
            )}
        </div>
    );
};

export default CommunityPage;
